import React from "react";
import {
  Activity,
  Bot,
  Briefcase,
  Calendar,
  ChevronRight,
  CreditCard,
  FileText,
  Gauge,
  HardDrive,
  Shield,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
  Crown,
  Clock,
  CheckCircle2,
  Lock,
  AlertTriangle,
  PieChart,
  Gift,
  Share2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useSubscriptionUsage, AgentUsageInfo } from "@/hooks/useSubscriptionUsage";
import { useNavigate } from "react-router-dom";
import PlanSelectionModal from "@/components/modals/PlanSelectionModal";
import { ReferralModal } from "@/components/modals/ReferralModal";
import { usePlansWithFeatures } from "@/hooks/use-plans-data";

/* ─── Helpers ──────────────────────────────────────────── */

const fmt = (n: number) => n.toLocaleString("es-CO");

const pctColor = (pct: number) =>
  pct >= 85
    ? "text-rose-600"
    : pct >= 60
    ? "text-amber-500"
    : "text-emerald-500";

const pctBarColor = (pct: number) =>
  pct >= 85
    ? "bg-rose-500"
    : pct >= 60
    ? "bg-amber-500"
    : "bg-emerald-500";

const statusBadge = (status: string, isTrial: boolean) => {
  if (isTrial)
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200 font-bold text-[10px] uppercase tracking-widest">
        Prueba Gratuita
      </Badge>
    );
  switch (status) {
    case "active":
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-bold text-[10px] uppercase tracking-widest">
          Activo
        </Badge>
      );
    case "past_due":
      return (
        <Badge className="bg-rose-100 text-rose-700 border-rose-200 font-bold text-[10px] uppercase tracking-widest">
          Pago Pendiente
        </Badge>
      );
    case "cancelled":
      return (
        <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-bold text-[10px] uppercase tracking-widest">
          Cancelado
        </Badge>
      );
    default:
      return (
        <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-bold text-[10px] uppercase tracking-widest">
          {status}
        </Badge>
      );
  }
};

const agentIcon: Record<string, React.ReactNode> = {
  "legal-chatbot": <Bot className="h-5 w-5" />,
  "contract-review": <Shield className="h-5 w-5" />,
  "legal-research": <FileText className="h-5 w-5" />,
  "document-generator": <FileText className="h-5 w-5" />,
  "document-drafting": <FileText className="h-5 w-5" />,
  "case-prediction": <TrendingUp className="h-5 w-5" />,
  "compliance-monitoring": <Shield className="h-5 w-5" />,
  "patent-search": <Sparkles className="h-5 w-5" />,
  "whistleblower": <AlertTriangle className="h-5 w-5" />,
  "consumer-assistant": <Users className="h-5 w-5" />,
};

const featureLabel: Record<string, string> = {
  "legal-chatbot": "Chat Legal",
  "contract-review": "Revisión de Contratos",
  "legal-research": "Investigación Jurídica",
  "document-generator": "Generador de Documentos",
  "document-drafting": "Redacción de Documentos",
  "case-prediction": "Predicción de Casos",
  "compliance-monitoring": "Monitoreo de Cumplimiento",
  "patent-search": "Búsqueda de Patentes",
  "whistleblower": "Canal de Denuncias",
  "consumer-assistant": "Asistente al Consumidor",
};

const formatDate = (d: string) => {
  return new Date(d).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (d: string) => {
  return new Date(d).toLocaleTimeString("es-CO", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ─── Radial Gauge ─────────────────────────────────────── */

const RadialGauge: React.FC<{
  value: number;
  max: number;
  label: string;
  icon: React.ReactNode;
  color?: string;
  sublabel?: string;
}> = ({ value, max, label, icon, sublabel }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const circumference = 2 * Math.PI * 40;
  const strokeDash = (pct / 100) * circumference;
  const colorClass = pctColor(pct);

  return (
    <Card className="rounded-[1.5rem] border-slate-200/60 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden">
      <CardContent className="p-5 flex flex-col items-center gap-3">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="currentColor"
              className="text-slate-100"
              strokeWidth="6"
            />
            <circle
              cx="48"
              cy="48"
              r="40"
              fill="none"
              stroke="currentColor"
              className={colorClass}
              strokeWidth="6"
              strokeDasharray={`${strokeDash} ${circumference}`}
              strokeLinecap="round"
              style={{ transition: "stroke-dasharray 1s ease-in-out" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={cn("text-lg font-black", colorClass)}>
              {Math.round(pct)}%
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-slate-400">{icon}</div>
        <p className="text-xs font-bold text-slate-900 text-center leading-tight">
          {label}
        </p>
        <p className="text-[11px] text-slate-400 font-medium">
          {fmt(value)} / {fmt(max)}
          {sublabel && <span className="ml-1">{sublabel}</span>}
        </p>
      </CardContent>
    </Card>
  );
};

/* ─── Agent Row ────────────────────────────────────────── */

const AgentUsageRow: React.FC<{ info: AgentUsageInfo }> = ({ info }) => {
  const pct = info.usagePercentage;
  return (
    <div className="flex items-center gap-4 py-3.5 px-4 rounded-2xl hover:bg-slate-50/80 transition-colors group">
      <div
        className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
          pct >= 85
            ? "bg-rose-50 text-rose-600"
            : pct >= 60
            ? "bg-amber-50 text-amber-600"
            : "bg-indigo-50 text-indigo-600"
        )}
      >
        {agentIcon[info.agent.id] || <Bot className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 truncate">
          {info.agent.name}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-700 ease-out",
                pctBarColor(pct)
              )}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <span
            className={cn("text-xs font-bold min-w-[36px] text-right", pctColor(pct))}
          >
            {Math.round(pct)}%
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-black text-slate-900">
          {info.dailyUsed}
          <span className="text-slate-400 font-medium">/{info.dailyLimit}</span>
        </p>
        <p className="text-[10px] text-slate-400 font-medium">hoy</p>
      </div>
    </div>
  );
};

/* ─── Main Page ────────────────────────────────────────── */

export default function SubscriptionUsage() {
  const data = useSubscriptionUsage();
  const navigate = useNavigate();
  const [showPlansModal, setShowPlansModal] = React.useState(false);
  const [showReferralModal, setShowReferralModal] = React.useState(false);
  const { data: plans } = usePlansWithFeatures();

  if (data.loading) {
    return (
      <div className="p-8 min-h-screen bg-[#fdfdfd] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-100 flex items-center justify-center animate-pulse">
            <Gauge className="h-6 w-6 text-indigo-600" />
          </div>
          <p className="text-sm font-bold text-slate-400">
            Cargando datos de uso...
          </p>
        </div>
      </div>
    );
  }

  if (!data.subscription || !data.plan) {
    return (
      <div className="p-8 min-h-screen bg-[#fdfdfd] flex items-center justify-center">
        <Card className="rounded-[2rem] border-slate-200 max-w-lg w-full text-center">
          <CardContent className="p-10 space-y-5">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto">
              <CreditCard className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-black text-slate-900">
              Sin Suscripción Activa
            </h2>
            <p className="text-slate-500 text-sm">
              No tienes un plan activo en este momento. Explora nuestros planes
              para comenzar a usar los agentes de IA legal.
            </p>
            <Button
              className="rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 font-bold"
              onClick={() => setShowPlansModal(true)}
            >
              <Crown className="h-4 w-4 mr-2" />
              Suscribirse Ahora
            </Button>
          </CardContent>
        </Card>
        <PlanSelectionModal
          isOpen={showPlansModal}
          onClose={() => setShowPlansModal(false)}
          plans={plans || []}
        />
      </div>
    );
  }

  const creditBalance = data.userCredits?.total_balance ?? 120;
  const creditLimit = data.monthlyCreditsLimit || 120;
  const creditPct = creditLimit > 0 ? (creditBalance / creditLimit) * 100 : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#fdfdfd] min-h-screen">
      {/* ─── Header ──────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Gauge className="h-7 w-7 sm:h-8 sm:w-8 text-indigo-600" />
            Mi <span className="text-indigo-600">Suscripción</span>
          </h1>
          <p className="text-slate-500 font-medium mt-1 text-sm">
            Resumen de tu plan, uso de agentes y consumo actual.
          </p>
        </div>
        <Button
          className="rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 gap-2 font-bold"
          onClick={() => setShowPlansModal(true)}
        >
          <Crown className="h-4 w-4" />
          Cambiar Plan
        </Button>
      </div>

      {/* ─── Section 1: Plan Overview Hero ────────────────── */}
      <Card className="rounded-[2rem] border-slate-200/60 overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-violet-600 p-5 sm:p-7 text-white relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtNGgydjRoNHYyaC00djRoLTJ2LTR6bS0yMi0yaDJ2LTRoLTJ2NGgtNHYyaDR2NGgydi00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-black">
                  Plan {data.plan.name}
                </h2>
                {statusBadge(data.subscription.status, data.subscription.is_trial)}
              </div>
              <p className="text-blue-100 text-sm font-medium">
                ${fmt(data.plan.price_per_month)}{" "}
                <span className="text-blue-200">/mes</span>
                {data.plan.interval === "year" && (
                  <span className="ml-2 text-blue-200">(facturación anual)</span>
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {data.subscription.is_trial && data.subscription.trial_ends_at && (
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                  <Clock className="h-4 w-4 text-amber-300" />
                  <div>
                    <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">
                      Prueba finaliza
                    </p>
                    <p className="text-sm font-black">
                      {formatDate(data.subscription.trial_ends_at)}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
                <Calendar className="h-4 w-4 text-blue-200" />
                <div>
                  <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">
                    {data.daysRemaining > 0 ? "Días restantes" : "Renovación"}
                  </p>
                  <p className="text-sm font-black">
                    {data.daysRemaining > 0
                      ? `${data.daysRemaining} días`
                      : data.subscription.ends_at
                      ? formatDate(data.subscription.ends_at)
                      : "—"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly progress bar */}
          <div className="relative mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest">
                Saldo de Créditos
              </p>
              <p className="text-xs font-bold text-white">
                {fmt(creditBalance)} / {fmt(creditLimit)}{" "}
                créditos disponibles
              </p>
            </div>
            <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${Math.min(creditPct, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </Card>

            {/* ─── Banner: Programa de Referidos ───────────────── */}
      <div className="p-5 sm:p-6 rounded-[2rem] bg-gradient-to-r from-violet-600 via-indigo-600 to-indigo-700 text-white shadow-lg shadow-indigo-100 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center shrink-0 border border-white/20">
            <Gift className="h-6 w-6 text-amber-300" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base sm:text-lg font-black">Refiere MiA a un amigo</span>
              <Badge className="bg-amber-400 text-slate-950 font-black text-[10px] px-2 py-0 border-none">
                +$10.000 COP
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-indigo-100 font-medium">
              Por cada amigo que se suscriba y adquiera un plan, recibes $10.000 de descuento en tu renovación del próximo mes. ¡Puedes referir sin límites!
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowReferralModal(true)}
          className="bg-white hover:bg-slate-100 text-indigo-900 font-bold rounded-xl px-5 h-11 shrink-0 shadow-md transition-all flex items-center gap-2"
        >
          <Share2 className="w-4 h-4 text-indigo-600" />
          Invitar Amigos y Ahorrar
        </Button>
      </div>

      {/* ─── Section 2: Usage KPI Cards ──────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <RadialGauge
          value={creditBalance}
          max={creditLimit}
          label="Créditos Restantes"
          icon={<Zap className="h-4 w-4" />}
        />
        <RadialGauge
          value={data.planAgents.length}
          max={data.plan.max_agents_included}
          label="Agentes Activos"
          icon={<Bot className="h-4 w-4" />}
        />
        <RadialGauge
          value={data.documentsUsedThisMonth}
          max={data.plan.max_documents_per_month}
          label="Documentos / Mes"
          icon={<FileText className="h-4 w-4" />}
        />
        <RadialGauge
          value={data.creditsUsedThisMonth}
          max={creditLimit}
          label="Créditos Usados / Mes"
          icon={<Activity className="h-4 w-4" />}
        />
        <RadialGauge
          value={data.procesosMetrics?.activeProcesses ?? data.currentCases}
          max={data.maxCases || data.plan.max_cases || 45}
          label="Procesos Activos"
          icon={<Briefcase className="h-4 w-4" />}
        />
      </div>

      {/* ─── Section 3: Despachos Quotas ─────────────────── */}
      <Card className="rounded-[2rem] border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-black text-slate-900 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-emerald-600" />
            Cuotas de Despacho
          </CardTitle>
          <CardDescription className="text-slate-500 text-xs font-medium">
            Límites de gestión para tu práctica legal.
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Cases */}
            <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Briefcase className="h-4 w-4 text-blue-600" />
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Casos
                </p>
              </div>
              <p className="text-2xl font-black text-slate-900">
                {data.currentCases}
                <span className="text-sm text-slate-400 font-medium ml-1">
                  / {data.maxCases}
                </span>
              </p>
              <Progress
                value={
                  data.maxCases > 0
                    ? (data.currentCases / data.maxCases) * 100
                    : 0
                }
                className="h-1.5 mt-2"
              />
            </div>

            {/* Clients */}
            <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                  <Users className="h-4 w-4 text-violet-600" />
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Clientes
                </p>
              </div>
              <p className="text-2xl font-black text-slate-900">
                {data.currentClients}
                <span className="text-sm text-slate-400 font-medium ml-1">
                  / {data.maxClients}
                </span>
              </p>
              <Progress
                value={
                  data.maxClients > 0
                    ? (data.currentClients / data.maxClients) * 100
                    : 0
                }
                className="h-1.5 mt-2"
              />
            </div>

            {/* Team */}
            <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Users className="h-4 w-4 text-amber-600" />
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Equipo
                </p>
              </div>
              <p className="text-2xl font-black text-slate-900">
                {data.teamMembers}
                <span className="text-sm text-slate-400 font-medium ml-1">
                  / {data.maxTeamMembers}
                </span>
              </p>
              <Progress
                value={
                  data.maxTeamMembers > 0
                    ? (data.teamMembers / data.maxTeamMembers) * 100
                    : 0
                }
                className="h-1.5 mt-2"
              />
            </div>

            {/* Storage */}
            <div className="p-4 rounded-2xl bg-slate-50/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <HardDrive className="h-4 w-4 text-emerald-600" />
                </div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Almacenamiento
                </p>
              </div>
              <p className="text-2xl font-black text-slate-900">
                {data.storageUsage.total_mb >= 1024
                  ? `${(data.storageUsage.total_mb / 1024).toFixed(2)} GB`
                  : `${data.storageUsage.total_mb.toFixed(1)} MB`}
                <span className="text-sm text-slate-400 font-medium ml-1">
                  / {data.storageUsage.quota_gb} GB
                </span>
              </p>
              <Progress
                value={Math.min(data.storageUsage.percentage, 100)}
                className="h-1.5 mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Section 5: Estado de los Procesos ────────────── */}
      <Card className="rounded-[2rem] border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-black text-slate-900 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-indigo-600" />
            Estado de los Procesos
          </CardTitle>
          <CardDescription className="text-slate-500 text-xs font-medium">
            Resumen operativo y vencimientos de tus expedientes.
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          <div className="grid gap-4 md:grid-cols-3">
            {/* Vencimientos y Términos */}
            <div className="flex flex-col items-center p-4 bg-rose-50/50 rounded-2xl border border-rose-100/80">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-2">
                <Clock className="h-5 w-5 text-rose-600" />
              </div>
              <p className="text-2xl font-black text-rose-700">{data.procesosMetrics?.upcomingDeadlines || 0}</p>
              <p className="text-xs font-bold text-rose-600/80 text-center uppercase tracking-wider mt-1">
                Vencimientos <br/>(Próx. 7 días)
              </p>
            </div>

            {/* Procesos Estancados */}
            <div className="flex flex-col items-center p-4 bg-amber-50/50 rounded-2xl border border-amber-100/80">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <p className="text-2xl font-black text-amber-700">{data.procesosMetrics?.stalledProcesses || 0}</p>
              <p className="text-xs font-bold text-amber-600/80 text-center uppercase tracking-wider mt-1">
                Inactivos <br/>(&gt;30 días)
              </p>
            </div>

            {/* Distribución por Estado */}
            <div className="flex flex-col justify-center p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100/80">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-bold text-emerald-700">Distribución</span>
                </div>
              </div>
              <div className="space-y-3 w-full">
                <div>
                  <div className="flex justify-between items-center mb-1 text-xs font-medium text-emerald-800">
                    <span>Activos</span>
                    <span>{data.procesosMetrics?.activeProcesses || 0}</span>
                  </div>
                  <Progress 
                    value={(data.procesosMetrics?.activeProcesses || 0) + (data.procesosMetrics?.finishedProcesses || 0) > 0 
                      ? ((data.procesosMetrics?.activeProcesses || 0) / ((data.procesosMetrics?.activeProcesses || 0) + (data.procesosMetrics?.finishedProcesses || 0))) * 100 
                      : 0} 
                    className="h-1.5" 
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1 text-xs font-medium text-emerald-800">
                    <span>Terminados/Archivados</span>
                    <span>{data.procesosMetrics?.finishedProcesses || 0}</span>
                  </div>
                  <Progress 
                    value={(data.procesosMetrics?.activeProcesses || 0) + (data.procesosMetrics?.finishedProcesses || 0) > 0 
                      ? ((data.procesosMetrics?.finishedProcesses || 0) / ((data.procesosMetrics?.activeProcesses || 0) + (data.procesosMetrics?.finishedProcesses || 0))) * 100 
                      : 0} 
                    className="h-1.5" 
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="rounded-[2rem] border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-black text-slate-900 flex items-center gap-2">
            <Bot className="h-5 w-5 text-indigo-600" />
            Uso por Agente
          </CardTitle>
          <CardDescription className="text-slate-500 text-xs font-medium">
            Consumo diario de cada agente de IA incluido en tu plan.
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-4">
          {data.agentUsage.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {data.agentUsage.map((info) => (
                <AgentUsageRow key={info.agent.id} info={info} />
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <Bot className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400 font-medium">
                No hay agentes asignados a este plan.
              </p>
            </div>
          )}
        </CardContent>
      </Card>



      {/* ─── Section 6: Plan Features ────────────────────── */}
      <Card className="rounded-[2rem] border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-black text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Características del Plan
          </CardTitle>
          <CardDescription className="text-slate-500 text-xs font-medium">
            Funcionalidades incluidas en tu plan actual.
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-6">
          {data.planFeatures.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.planFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100/60"
                >
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">
                      {feature.name}
                    </p>
                    {feature.description && (
                      <p className="text-[11px] text-slate-400 truncate">
                        {feature.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <Lock className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <p className="text-sm text-slate-400 font-medium">
                No hay características definidas para este plan.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      <PlanSelectionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        plans={plans || []}
      />
      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />

    </div>
  );
}
